import { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import FabGroup from '../components/FabGroup';
import EntityTable from '../components/Listing/EntityTable';
import GenerateReportModal from '../components/Modal/GenerateReportModal';
import ConfirmModal from '../components/Modal/ConfirmModal';
import InputText from '../components/Form/Fields/InputText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faDownload, faTrash, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import * as pdfjsLib from 'pdfjs-dist';
import PdfReader from '../components/PdfReader/PdfReader';
import { formatRelativeDate, formatFullDate } from '../utils/FormatDate';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const stringToColor = (str = '') => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    let color = '#';
    for (let i = 0; i < 3; i++) color += (`00${((hash >> (i * 8)) & 0xFF).toString(16)}`).slice(-2);
    return color;
};

const userInitials = (name = '') => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

const shortName = (name = '') => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) return name;
    return `${words[0]} ${words[1][0].toUpperCase()}.`;
};

const UserAvatar = ({ name }) => (
    <span style={{
        width: 24, height: 24, borderRadius: 8, flexShrink: 0,
        background: stringToColor(name || ''), color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 10, letterSpacing: '0.02em',
    }}>
        {userInitials(name)}
    </span>
);

export default function Reports() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const { hasRight } = useAuth();
    const canCreate = hasRight('REPORTS_CREATE');
    const canDelete = hasRight('REPORTS_DELETE');
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const tableRef = useRef(null);

    const [pdfData, setPdfData] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfReport, setPdfReport] = useState(null);

    useEffect(() => {
        setTitle(`${t('Reports')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('reports.subtitle')}</span>`);
        document.title = t('Reports');
        setTitleIcon('ti-report-analytics');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Reports'), url: '/reports' }]);

    const handleViewPdf = async (row) => {
        // Open the reader right away with no fileUrl yet — PdfReader shows its own
        // "Chargement du document…" spinner in that state, covering the download delay
        // instead of leaving the click looking like it did nothing for a few seconds.
        setPdfData(null);
        setPdfReport(row);
        setShowPdfModal(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/v1/reports/${row.id}/download`, {
                withCredentials: true,
                responseType: 'blob',
            });
            const fileURL = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            setPdfData(fileURL);
        } catch (e) {
            console.error(e);
            toast.error(t('reports.toast.preview_error'));
            setShowPdfModal(false);
        }
    };

    const downloadReport = async (row) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/v1/reports/${row.id}/download`, {
                withCredentials: true,
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `rapport-${row.id}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            toast.error(t('reports.toast.download_error'));
        }
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/api/v1/reports/${deleteTarget.id}`, { withCredentials: true });
            toast.success(t('reports.toast.deleted'));
            tableRef.current?.reload();
        } catch (e) {
            console.error(e);
            toast.error(t('reports.toast.delete_error'));
        } finally {
            setDeleteTarget(null);
        }
    };

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            className: 'text-center text-nowrap',
            width: '1%',
            filterable: { component: InputText },
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: t('Name'),
            accessor: 'name',
            // the only column that grows: the others shrink to their content (width 1% + nowrap)
            width: '100%',
            Cell: (row) => (
                <span className="cell-name" style={{ cursor: 'pointer' }}>
                    {row.name || t('reports.default_name', { id: row.id })}
                    <span
                        className="cell-sub"
                        data-tooltip-id="tooltip"
                        data-tooltip-content={formatFullDate(row.createdAt)}
                    >
                        {t('reports.generated_relative', { date: formatRelativeDate(row.createdAt) })}
                    </span>
                </span>
            ),
        },
        {
            Header: t('reports.column.type'),
            accessor: 'typeLabel',
            className: 'text-nowrap',
            width: '1%',
            // plain text, like the Période column next to it
            Cell: (row) => <span style={{ fontSize: 13, color: '#374151' }}>{row.typeLabel}</span>,
        },
        {
            Header: t('reports.column.period'),
            accessor: 'periodFrom',
            className: 'text-nowrap',
            width: '1%',
            Cell: (row) => (
                <span style={{ fontSize: 13, color: '#374151' }}>
                    {new Date(row.periodFrom).toLocaleDateString('fr-MA')} — {new Date(row.periodTo).toLocaleDateString('fr-MA')}
                </span>
            ),
        },
        {
            Header: t('reports.column.created_by'),
            accessor: 'createdByName',
            className: 'text-nowrap',
            width: '1%',
            Cell: (row) => row.createdByName
                ? (
                    <div className="d-flex align-items-center gap-2">
                        <UserAvatar name={row.createdByName} />
                        <span style={{ fontSize: 13, color: '#374151' }}>{shortName(row.createdByName)}</span>
                    </div>
                )
                : <span className="cell-muted">—</span>,
        },
        {
            Header: '',
            accessor: '_actions',
            className: 'col-actions',
            width: '1%',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); downloadReport(row); }}>
                                <FontAwesomeIcon icon={faDownload} className="me-2 text-primary" />
                                {t('reports.action.download_pdf')}
                            </button>
                        </li>
                        {canDelete && (
                            <li>
                                <button className="dropdown-item danger" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}>
                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                    {t('Delete')}
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === '_actions');
    const renderReportCard = (row) => {
        return (
            <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => handleViewPdf(row)}>
                <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                    {/* the type as plain text, like the table's Type column */}
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{row.typeLabel}</span>

                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', marginTop: 4 }}>
                        {row.name || t('reports.default_name', { id: row.id })}
                    </span>
                    <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
                        {new Date(row.periodFrom).toLocaleDateString('fr-MA')} — {new Date(row.periodTo).toLocaleDateString('fr-MA')}
                    </span>

                    {row.createdByName && (
                        <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
                            <UserAvatar name={row.createdByName} />
                            <span style={{ fontSize: 12.5, color: '#6b7280' }}>{shortName(row.createdByName)}</span>
                        </div>
                    )}

                    <div className="d-flex align-items-center justify-content-end" style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <div onClick={(e) => e.stopPropagation()}>
                            {actionsColumn?.Cell(row)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const fabButtons = canCreate ? [
        {
            id: 'generate-report',
            label: t('reports.generate'),
            icon: faPlus,
            onClick: () => setShowGenerateModal(true),
        },
    ] : [];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/reports`}
                columns={columns}
                defaultSort="createdAt"
                defaultDirection="desc"
                pageSize={20}
                searchable={false}
                multiSelect={true}
                emptyMessage={t('reports.empty')}
                onRowClick={(row) => handleViewPdf(row)}
                enableCardView={true}
                renderCard={renderReportCard}
            />

            <FabGroup buttons={fabButtons} />

            <GenerateReportModal
                show={showGenerateModal}
                onClose={() => setShowGenerateModal(false)}
                onGenerated={(report) => { tableRef.current?.reload(); handleViewPdf(report); }}
            />

            <ConfirmModal
                show={!!deleteTarget}
                title={t('reports.delete_title')}
                description={deleteTarget ? t('reports.delete_confirm', { type: deleteTarget.typeLabel }) : ''}
                confirmLabel={t('Delete')}
                confirmVariant="danger"
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
            />

            <PdfReader
                show={showPdfModal}
                fileUrl={pdfData}
                fileName={pdfReport ? `rapport-${pdfReport.id}.pdf` : 'rapport.pdf'}
                onClose={() => setShowPdfModal(false)}
            />
        </>
    );
}
